import { create } from 'zustand';
import { restoreProductRedirect } from '../utils/productRedirect';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { LayoutModeEnum, EnableModeEnum } from '../type/modeState.type';

export const useUserStore = create(
    devtools(
        persist(
            (set) => ({
                partnerId: null,
                partnerType: null,
                chatUserId: null,
                isMobileDevice: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
                
                setIsMobileDevice: (isMobileDevice) => set({ isMobileDevice }),
                setPartnerId: (partnerId) => set({ partnerId }),
                setPartnerType: (partnerType) => set({ partnerType }),
                setChatUserId: (chatUserId) => set({ chatUserId }),
            }),
            {
                name: 'user-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);

export const useChatbotStore = create(
    devtools(
        persist(
            (set) => ({
                chatbot: null,
                bootConfigGreetingComment: '',
                setChatbot: (chatbot) => set({ chatbot }),
                setBootConfigGreetingComment: (bootConfigGreetingComment) => set({ bootConfigGreetingComment }),
                clearBootConfigGreetingComment: () => set({ bootConfigGreetingComment: '' })
            }),
            {
                name: 'chatbot-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);

export const useChatStateStore = create(
    devtools(
        persist(
        set => ({
            isChatbotPending: false,
            isNewProduct: false,
            newProductCount: 0,
            currentItemId: [],
            currentShrinkCardIndex: 0,
            currentListCardIndex: 0,
            isPreAnswered: false,
            isCsFlowActive: false,
            isFormSubmitted: 'initial', // initial, ready, submitted
            formAutoStartOnce: false, // one-shot: only for specific triggers (e.g., csInquiryButton)
            isExceptional: false,
            isConsole: false,
            isStreaming: false,
            connectionId: null,
            displayLocation: null,
            displayItemId: null,
            isPDPChat: false,
            currentPage: null,
            enableMode: EnableModeEnum.DEFAULT,
            isChatOpen: false,
            language: 'ko',
            isInConsoleTour: false,
            customizedGreeting: null,
            layoutMode: LayoutModeEnum.MODAL,
            addToCartWithOption: false,  // FlowLift project: B variant - false, C variant - true, default is false
            isLastIntentProductRecommendation: false,   // one of the intents: '제공', '추천', '딥리서치'
            autoUserMessage: null,  // postMessage로 전달된 자동 메시지 (검색 트리거 등)
            variant: null,
            fastfiveScenarioId: null,   // fastfive entry 버튼 선택값 ('office'|'interior'|'building'|'private'|'lounge')
            // scenarioId 가 도메인 이동(URL ?entry= / SDK postMessage)으로 세팅되었는지 여부.
            // true 일 때만 시나리오 greetingMessage 를 노출 (entry 버튼 클릭으로 들어온 경우엔 숨김).
            isScenarioAutoEntered: false,
            setIsChatbotPending: (isChatbotPending) => set({ isChatbotPending }),
            setIsNewProduct: (isNewProduct) => set({ isNewProduct }),
            setNewProductCount: (newProductCount) => set({ newProductCount }),
            setCurrentItemId: (currentItemId) => {
                set({ currentItemId })
            },
            setCurrentShrinkCardIndex: (currentShrinkCardIndex) => set({ currentShrinkCardIndex }),
            setCurrentListCardIndex: (currentListCardIndex) => set({ currentListCardIndex }),
            setIsPreAnswered: (isPreAnswered) => set({ isPreAnswered }),
            setIsFormSubmitted: (isFormSubmitted) => set({ isFormSubmitted }),
            setFormAutoStartOnce: (formAutoStartOnce) => set({ formAutoStartOnce }),
            clearFormAutoStartOnce: () => set({ formAutoStartOnce: false }),
            setIsExceptional: (isExceptional) => set({ isExceptional }),
            setIsConsole: (isConsole) => set({ isConsole }),
            setIsStreaming: (isStreaming) => set({ isStreaming }),
            setConnectionId: (connectionId) => set({ connectionId }),
            setDisplayLocation: (displayLocation) => set({ displayLocation }),
            setDisplayItemId: (displayItemId) => set({ displayItemId }),
            setIsPDPChat: (isPDPChat) => set({ isPDPChat }),
            setCurrentPage: (currentPage) => set({ currentPage }),
            setEnableMode: (enableMode) => set({ enableMode }),
            setIsChatOpen: (isChatOpen) => set({ isChatOpen }),
            setLanguage: (language) => set({ language }),
            setIsInConsoleTour: (isInConsoleTour) => set({ isInConsoleTour }),
            setCustomizedGreeting: (customizedGreeting) => set({ customizedGreeting }),
            clearCustomizedGreeting: () => set({ customizedGreeting: null }),
            clearIsInConsoleTour: () => set({ isInConsoleTour: false }),
            clearCurrentShrinkCardIndex: () => set({ currentShrinkCardIndex: 0 }),
            clearCurrentListCardIndex: () => set({ currentListCardIndex: 0 }),
            setLayoutMode: (layoutMode) => set({ layoutMode }),
            setAddToCartWithOption: (addToCartWithOption) => set({ addToCartWithOption }),
            setIsLastIntentProductRecommendation: (isLastIntentProductRecommendation) => set({ isLastIntentProductRecommendation }),
            setIsCsFlowActive: (isCsFlowActive) => set({ isCsFlowActive }),
            setAutoUserMessage: (autoUserMessage) => set({ autoUserMessage }),
            clearAutoUserMessage: () => set({ autoUserMessage: null }),
            setVariant: (variant) => set({ variant }),
            // 두 번째 인자 isScenarioAutoEntered:
            //   - true  : 도메인 이동(URL/SDK postMessage)으로 진입 → scenario greeting 노출 대상
            //   - false : entry 버튼 클릭으로 진입 → scenario greeting 숨김 (기본값)
            setFastfiveScenarioId: (fastfiveScenarioId, isScenarioAutoEntered = false) =>
                set({ fastfiveScenarioId, isScenarioAutoEntered }),
            clearFastfiveScenarioId: () => set({ fastfiveScenarioId: null, isScenarioAutoEntered: false }),
        }),
        {
            name: 'chat-state-storage',
            storage: createJSONStorage(() => sessionStorage),
        }
    )
)
);

export const useMessagesStore = create(
    devtools(
        persist(
        set => ({
            messages: [],
            streamMessage: '',
            isBuilder: false,
            guidelineId: null,
            guidelineContent: '',
            guidelineTitle: '',
            setMessages: (messages) => set((prev) => {
                const newMessages = Array.isArray(messages) ? messages : [messages];
                // 스켈레톤이면 대체하기
                if (prev.messages.length > 0 && prev.messages[prev.messages.length - 1].isLoading) {
                    return {
                        messages: [...prev.messages.slice(0, -1), ...newMessages]
                    }
                }
                return {
                    messages: [...prev.messages, ...newMessages]
                }
            }),
            clearMessages: () => set({ messages: [] }),
            setStreamMessage: (streamMessage) => set((prev) => {
                return {
                    streamMessage: prev.streamMessage + streamMessage
                }
            }),
            clearStreamMessage: () => set({ streamMessage: '' }),
            setIsBuilder: (isBuilder) => set({ isBuilder }),
            setGuidelineId: (guidelineId) => set({ guidelineId }),
            setGuidelineContent: (guidelineContent) => set({ guidelineContent }),
            setGuidelineTitle: (guidelineTitle) => set({ guidelineTitle }),
            clearGuidelineId: () => set({ guidelineId: null }),
            clearGuidelineContent: () => set({ guidelineContent: '' }),
            clearGuidelineTitle: () => set({ guidelineTitle: '' }),
        }),
        {
            name: 'messages-storage',
            storage: createJSONStorage(() => sessionStorage),
        }
        )
    )
);

export const useProductsStore = create(
    devtools(
        persist(
            (set) => ({
                products: [],
                examples: [],
                followupQuestion: [],
                structuredProducts: [],
                setProducts: (products) => set((prev) => {
                    return {
                        products: [...products.map(product => ({ ...product, productUrl: restoreProductRedirect(product.productUrl) })), ...prev.products]
                    }
                }),
                setExamples: (examples) => set({ examples }),
                setFollowupQuestion: (followupQuestion) => set({ followupQuestion }),
                setStructuredProducts: (structuredProducts) => set((prev) => {
                    return {
                        structuredProducts: [...structuredProducts.map(group => group.map(product => ({ ...product, productUrl: restoreProductRedirect(product.productUrl) }))), ...prev.structuredProducts]
                    }
                }),
                clearProducts: () => set({ products: [] }),
                clearExamples: () => set({ examples: [] }),
                clearFollowupQuestion: () => set({ followupQuestion: [] }),
                clearStructuredProducts: () => set({ structuredProducts: [] }),
            }),
            {
                name: 'products-storage',
                storage: createJSONStorage(() => sessionStorage, {
                    replacer: (key, value) => {
                        // 직접 serialize
                        if (key === 'products' && Array.isArray(value)) {
                            
                            return value.map(product => ({
                                ...product,
                                variantList: Array.isArray(product.variantList) ? product.variantList : [],
                                prepaidShipping: product.prepaidShipping ?? '',
                                category: Array.isArray(product.category) ? product.category : [],
                                currency: product.currency || 'KRW',
                                idx: Number(product.idx) || 0,
                            }));
                        }
                        
                        return value;
                    },
                    reviver: (key, value) => {
                        // 직접 deserialize
                        if (key === 'productUrl') return restoreProductRedirect(value);
                        if (key === 'products' && Array.isArray(value)) {
                            
                            return value.map(product => ({
                                ...product,
                                variantList: Array.isArray(product.variantList) ? product.variantList : [],
                                prepaidShipping: product.prepaidShipping ?? '',
                                category: Array.isArray(product.category) ? product.category : [],
                                currency: product.currency || 'KRW',
                                idx: Number(product.idx) || 0,
                            }));
                        }
                        
                        return value;
                    }
                }),
                merge: (persistedState, currentState) => {
                    // persistance 주의하여 직접 merge
                    const mergedState = { ...currentState, ...persistedState };
                    if (persistedState?.products && Array.isArray(persistedState.products)) {
                        mergedState.products = persistedState.products.map(product => {
                            return {
                                ...product,
                                variantList: product.variantList || [],
                                prepaidShipping: product.prepaidShipping ?? '',
                                category: product.category || [],
                                currency: product.currency || 'KRW',
                                idx: product.idx ?? 0,
                            };
                        });
                    }
                    
                    return mergedState;
                },
            }
        )
    )
);

export const useFormStore = create(
    devtools(
        persist(
            (set) => ({
                formData: {},
                preAnswersObject: [],
                setFormData: (formData) => set({ formData }),
                setPreAnswersObject: (preAnswersObject) => set({ preAnswersObject }),
                clearPreAnswersObject: () => set({ preAnswersObject: [] }),
            }),
            {
                name: 'form-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);

export const useUtmStore = create(
    devtools(
        persist(
            set => ({
                utmParams: {},
                setUtmParams: (utmParams) => {
                    if (!utmParams) return;
                    // transitionPage를 제외한 값들만 체크
                    const utmKeys = Object.keys(utmParams).filter(key => key !== "transitionPage");
                    const isAllEmpty = utmKeys.every(key => {
                        const v = utmParams[key];
                        return v === undefined || v === null || v === "";
                    });
                    if (isAllEmpty) {
                        // utm 정보가 모두 비면 건너뜀
                        return;
                    }
                    set({ utmParams });
                },
            }),
            {
                name: 'utm-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);


export const useToastStore = create(
    devtools(
        persist(
            (set) => ({
                toastMessage: '',
                isToastVisible: false,
                setToastMessage: (toastMessage) => set({ toastMessage }),
                setIsToastVisible: (isToastVisible) => set({ isToastVisible }),
            }),
            {
                name: 'toast-storage',
                storage: createJSONStorage(() => sessionStorage),
            }
        )
    )
);